export interface ParsedYouTubeInput {
  videoId: string;
  initialSeconds: number;
}

const VIDEO_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const ALLOWED_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'youtu.be',
  'www.youtube-nocookie.com',
]);

function parseTime(value: string | null): number {
  if (!value) return 0;
  if (/^\d+(?:\.\d+)?$/.test(value)) return Math.max(0, Number(value));
  const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+(?:\.\d+)?)s)?$/i);
  if (!match || !match.slice(1).some(Boolean)) return 0;
  return Number(match[1] ?? 0) * 3600 + Number(match[2] ?? 0) * 60 + Number(match[3] ?? 0);
}

export function isValidVideoId(value: string): boolean {
  return VIDEO_ID_PATTERN.test(value);
}

export function parseYouTubeInput(input: string): ParsedYouTubeInput | null {
  const value = input.trim();
  if (isValidVideoId(value)) return { videoId: value, initialSeconds: 0 };

  try {
    const url = new URL(value);
    if (
      url.protocol !== 'https:' ||
      !ALLOWED_HOSTS.has(url.hostname.toLowerCase()) ||
      url.username ||
      url.password ||
      url.port
    ) return null;

    let videoId: string | null = null;
    if (url.hostname.toLowerCase() === 'youtu.be') {
      videoId = url.pathname.split('/').filter(Boolean)[0] ?? null;
    } else if (url.pathname === '/watch') {
      videoId = url.searchParams.get('v');
    } else {
      const parts = url.pathname.split('/').filter(Boolean);
      if (['shorts', 'embed'].includes(parts[0] ?? '')) videoId = parts[1] ?? null;
    }
    if (!videoId || !isValidVideoId(videoId)) return null;

    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const time = url.searchParams.get('t') ?? url.searchParams.get('start') ?? hashParams.get('t');
    return { videoId, initialSeconds: parseTime(time) };
  } catch {
    return null;
  }
}
